import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { Loader2, ShieldX } from "lucide-react";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { useSessionSecurity } from "@/hooks/useSessionSecurity";
import SessionTimeoutWarning from "@/components/auth/SessionTimeoutWarning";

interface AdminGuardProps {
  /** When used as a layout route, omit children — <Outlet /> renders automatically. */
  children?: React.ReactNode;
}

// Allowed admin roles
const ADMIN_ROLES = ["admin", "manager"] as const;

// Dev bypass: DISABLED — admin auth must always be enforced.
// Configure VITE_SUPABASE_URL in .env.local for local development.
const DEV_BYPASS = false;

export default function AdminGuard({ children }: AdminGuardProps) {
  const { t } = useTranslation("common");
  const [checking, setChecking] = useState(!DEV_BYPASS);
  const [authorized, setAuthorized] = useState(DEV_BYPASS);
  const [unauthorized, setUnauthorized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Capture the initial location for the returnUrl — don't re-run auth on navigation.
  // Strip any existing returnUrl to prevent nested redirect chains.
  const cleanInitialPath = (() => {
    const params = new URLSearchParams(location.search);
    params.delete("returnUrl");
    const cleanSearch = params.toString() ? `?${params.toString()}` : "";
    return location.pathname + cleanSearch;
  })();
  const initialPathRef = useRef(cleanInitialPath);

  const redirectToLogin = useCallback(() => {
    navigate(
      `/admin/connexion?returnUrl=${encodeURIComponent(initialPathRef.current)}`,
      { replace: true },
    );
  }, [navigate]);

  // Auth check runs ONCE on mount — not on every route change
  useEffect(() => {
    if (DEV_BYPASS) return;

    const checkAdminAccess = async () => {
      try {
        // Step 1: Verify authentication via server-validated JWT
        // Fail closed: if auth check takes >3s, redirect to login
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3_000);
        let user: Awaited<ReturnType<typeof supabase.auth.getUser>>["data"]["user"];
        try {
          const result = await Promise.race([
            supabase.auth.getUser(),
            new Promise<never>((_, reject) => {
              controller.signal.addEventListener("abort", () =>
                reject(new Error("Auth timeout"))
              );
            }),
          ]);
          user = result.data.user;
          if (result.error || !user) {
            redirectToLogin();
            return;
          }
        } catch {
          redirectToLogin();
          return;
        } finally {
          clearTimeout(timeout);
        }
        // Step 2: Check admin role + platform from admin_users table
        // Direction department has cross-platform access (cieda + djayma)
        const { data: adminUser, error: adminError } = await supabase
          .from("admin_users")
          .select("role, is_active, platform, department:departments(name)")
          .eq("id", user.id)
          .eq("is_active", true)
          .single();

        if (adminError || !adminUser) {
          setUnauthorized(true);
          return;
        }

        const isDjayma = adminUser.platform === "djayma";
        const isDirection =
          (adminUser.department as { name?: string } | null)?.name === "Direction";
        const hasAdminRole = ADMIN_ROLES.includes(
          adminUser.role as (typeof ADMIN_ROLES)[number]
        );

        if (hasAdminRole && (isDjayma || isDirection)) {
          setAuthorized(true);
        } else {
          setUnauthorized(true);
        }
      } catch {
        redirectToLogin();
      } finally {
        setChecking(false);
      }
    };

    checkAdminAccess();

    // Listen for auth state changes — only react to explicit sign-out events.
    // Reacting to ANY null-session event (including INITIAL_SESSION) can cause
    // false redirects when the session is still loading from storage.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" && !session) {
        setAuthorized(false);
        setUnauthorized(false);
        redirectToLogin();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [redirectToLogin]);

  // Redirect unauthorized users to homepage after a brief message
  useEffect(() => {
    if (!unauthorized) return;

    const timer = setTimeout(() => {
      navigate("/", { replace: true });
    }, 2000);

    return () => clearTimeout(timer);
  }, [unauthorized, navigate]);

  // ─── Session security: idle timeout, absolute timeout, multi-tab sync ───
  const handleSessionLogout = useCallback(async () => {
    await supabase.auth.signOut();
    // Hard redirect clears all React state — critical for admin sessions
    window.location.href = "/admin/connexion?reason=timeout";
  }, []);

  const handleValidateSession = useCallback(async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    return !!user && !error;
  }, []);

  const { isWarningVisible, remainingSeconds, extendSession, forceLogout } =
    useSessionSecurity({
      idleTimeoutMs: 15 * 60 * 1000, // 15 min — admin privilege = tighter timeout
      absoluteTimeoutMs: 8 * 60 * 60 * 1000, // 8 hours
      warningBeforeMs: 2 * 60 * 1000, // 2 min warning before idle logout
      onLogout: handleSessionLogout,
      validateSession: handleValidateSession,
      enabled: authorized, // Only active once admin role is verified
    });

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-secondary" />
          <p className="text-sm text-muted-foreground">{t("common.loading", "Chargement...")}</p>
        </div>
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center px-4">
          <ShieldX className="h-12 w-12 text-destructive" />
          <h2 className="text-lg font-semibold text-foreground">
            Accès non autorisé
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return null;
  }

  return (
    <AdminAuthProvider>
      {children ?? <Outlet />}
      <SessionTimeoutWarning
        open={isWarningVisible}
        remainingSeconds={remainingSeconds}
        onExtend={extendSession}
        onLogout={forceLogout}
      />
    </AdminAuthProvider>
  );
}
